import { Grid, Card, CardContent, Tooltip } from '@material-ui/core';
import React, { Fragment } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

const durationFromSeconds = (val) => { 
    if(!val) return "00:00:00"
    const hours =  Math.floor(val / 60 / 60);
    const minutes =  Math.floor((val / 60) % 60);
    const seconds =  Math.round(val % 60);
    return `${hours > 9 ? hours : "0" +hours}:${minutes>9 ? minutes : "0" + minutes}:${seconds>9 ? seconds : "0" + seconds}`
};

const getTitle = (title, tooltipText) => {
    const titleComponent = <small className="text-white-50 mr-3 text-uppercase">{title}</small>;
    if(tooltipText)
        return <Tooltip title={ tooltipText }><div>{titleComponent}</div></Tooltip>;
    return titleComponent;
}

const getAttributeRow = (title, value, delta = null) => {
    return (
    <Grid container spacing={0}>
        <Grid item xs={5} md={5}> {title} </Grid>
        <Grid item xs={4} md={3}> <span className="font-size-m "> {value} </span></Grid>
        <Grid item xs={3} md={3}> {delta} </Grid>
    </Grid>
    )
}

const getChange = (property, values, baseline) => { 
    if(!baseline) return null;
    const changeUp = <span key="changeUp" className="font-size-l"><FontAwesomeIcon icon={['fa', 'arrow-up']} className="font-size-l" /></span>
    const changeDown = <span key="changeDown" className="font-size-l"><FontAwesomeIcon icon={['fa', 'arrow-down']} className="font-size-l" /></span>
    const change = Math.round(values && baseline ? (values[property] / values.meetings) / (baseline[property] / baseline.meetings) * 100 - 100 : 0);
    if(change < 0)
        return <span key="changeDown" className="ml-3 badge bg-brand-slack">{changeDown}<span key="delta" className="font-size-l "> { change }% </span></span>;
    if(change > 0)
        return <span key="changeUp" className="ml-3 badge badge-danger">{changeUp}<span key="delta" className="font-size-l "> { change }% </span></span>;
    return <span key="delta" className="font-size-l mt-1"> </span>;
};

const getMeetingsPerDayChange = (property, values, baseline) => { 
    if(!baseline) return null;
    const changeUp = <span key="changeUp" className="font-size-l"><FontAwesomeIcon icon={['fa', 'arrow-up']} className="font-size-l" /></span>
    const changeDown = <span key="changeDown" className="font-size-l"><FontAwesomeIcon icon={['fa', 'arrow-down']} className="font-size-l" /></span>
    const change = Math.round(values && baseline ? (values[property]) / (baseline[property]) * 100 - 100 : 0);
    if(change < 0)
        return <span key="changeDown" className="ml-3 badge bg-brand-slack">{changeDown}<span key="delta" className="font-size-l "> { change }% </span></span>;
    if(change > 0)
        return <span key="changeUp" className="ml-3 badge badge-danger">{changeUp}<span key="delta" className="font-size-l "> { change }% </span></span>;
    return <span key="delta" className="font-size-l mt-1"> </span>;
};

const getAttributeGrid = (props) => {
    const duration = getAttributeRow(getTitle("Duration", "Average duration of meetings during the time period."), durationFromSeconds(props.values.duration / props.values.meetings * 60), getChange("duration", props.values, props.baseline));
    const invites = getAttributeRow(getTitle("Participants", "Average participants (invitees + organizer) of meetings during the time period."), Math.round(props.values.invites / props.values.meetings * 10) / 10, getChange("invites", props.values, props.baseline));
    const meetingsPerDay = getAttributeRow(getTitle("Meetings / Day", "Average number of meetings per day during the time period."), Math.round(props.values.meetingsPerDay * 10) / 10, getMeetingsPerDayChange("meetingsPerDay", props.values, props.baseline));
    const meetings = getAttributeRow(getTitle("Meetings", "Number of meetings during the time period."), props.values.meetings);
    const days = getAttributeRow(getTitle("Days", "Number of days included in this time period."), props.values.days);
    const componentTitle = props.title ? props.title : "No title";

    return (
        <Grid container spacing={0}>
            <Grid item xs={12} md={12}> <span className="text-white-90 font-size-l d-block mb-1 text-uppercase">{componentTitle}</span></Grid>
            <Grid item xs={12} md={12}> {duration} </Grid>
            <Grid item xs={12} md={12}> {invites} </Grid>
            <Grid item xs={12} md={12}> {meetingsPerDay} </Grid>
            <Grid item xs={12} md={12}> {meetings} </Grid>
            <Grid item xs={12} md={12}> {days} </Grid>
        </Grid>
    )
}

const getCostProps = (account) => {
    const costProps = account && account.props.costConfiguration ? account.props.costConfiguration : { hours: 1730, salary: 84217, hourly: 1730 / 84217, currency: "GBP" };
    if(!costProps.hourly) costProps.hourly = costProps.hours / (costProps.salary * 1000);
    return costProps;
}
function convertMinutesToCostString(minutes, account) {
    const costProps = getCostProps(account);
    const cost = minutes / 60 / costProps.hourly;
    if (!cost) return "-";
    const formatter = new Intl.NumberFormat(Intl.NumberFormat().resolvedOptions().locale, {
      style: 'currency',
      currency: costProps.currency,
      minimumFractionDigits: 0,
      maximumFractionDigits:0
    });
    return formatter.format(cost); // "£1,000.00"
}

const getRoiGrid = (props) => {
    const pDuration = props.values.duration / props.values.meetings;
    const pParticipants = props.values.invites / props.values.meetings;
    const pCost = pDuration * pParticipants
    const pMeetings = props.values.meetings / props.values.days;

    //const costExplain = `(Average duration ${roundToTwo(pDuration)} * average participants ${roundToTwo(pParticipants)} = ${roundToTwo(pCost)}) * (${roundToTwo(pMeetings)}) * (${convertMinutesToCostString(60, props.flowtraceUser.account)})`;
    const bDuration = props.baseline.duration / props.baseline.meetings;
    const bParticipants = props.baseline.invites / props.baseline.meetings;
    //const bMeetings = props.baseline.meetings / props.baseline.days;
    const bCost = bDuration * bParticipants
    const savings = bCost - pCost;

    const roundToTwo = (value) => Math.round(value * 100) / 100;
    const costExplain = `Formula: [avg duration * avg participants * avg meetings per day * avg employee hourly cost * period days = cost] (${roundToTwo(pDuration)} * ${roundToTwo(pParticipants)} = ${roundToTwo(pCost)}) * (${roundToTwo(pMeetings)}) * (${convertMinutesToCostString(60, props.flowtraceUser.account)}) * (1/30/365)`;
    const costBaselineExplain = `Formula: [baseline avg duration * baseline avg participants * avg meetings per day * avg employee hourly cost * period days = cost] (${roundToTwo(bDuration)} * ${roundToTwo(bParticipants)} = ${roundToTwo(bCost)}) * (${roundToTwo(pMeetings)}) * (${convertMinutesToCostString(60, props.flowtraceUser.account)}) * (1/30/365)`;
    const saveExplain = `Formula: [(baseline avg duration * baseline avg participants - avg duration * avg participants) * avg meetings per day * avg employee hourly cost * period days = savings] ((${roundToTwo(bDuration)} * ${roundToTwo(bParticipants)} = ${roundToTwo(bCost)}) - (${roundToTwo(pDuration)} * ${roundToTwo(pParticipants)} = ${roundToTwo(pCost)}) = ${roundToTwo(savings)} ) * (${roundToTwo(pMeetings)}) * (${convertMinutesToCostString(60, props.flowtraceUser.account)}) * (1/30/365)`;

    const day = {
        meetings: Math.round(pMeetings),
        cost: pMeetings * pCost,
        saving: pMeetings * (bCost - pCost),
        costString: convertMinutesToCostString(pMeetings * pCost, props.flowtraceUser.account),
        costBaselineString: convertMinutesToCostString(pMeetings * bCost, props.flowtraceUser.account),
        costSavingString: convertMinutesToCostString(pMeetings * (bCost - pCost), props.flowtraceUser.account),
    }
    const month = {
        meetings: Math.round(pMeetings * 30),
        cost: pMeetings * 30 * pCost,
        saving: pMeetings * 30 * (bCost - pCost),
        costString: convertMinutesToCostString(pMeetings * 30 * pCost, props.flowtraceUser.account),
        costBaselineString: convertMinutesToCostString(pMeetings * 30 * bCost, props.flowtraceUser.account),
        costSavingString: convertMinutesToCostString(pMeetings * 30 * (bCost - pCost), props.flowtraceUser.account),
    }
    const year = {
        meetings: Math.round(pMeetings * 365),
        cost: pMeetings * 365 * pCost,
        saving: pMeetings * 365 * (bCost - pCost),
        costString: convertMinutesToCostString(pMeetings * 365 * pCost, props.flowtraceUser.account),
        costBaselineString: convertMinutesToCostString(pMeetings * 365 * bCost, props.flowtraceUser.account),
        costSavingString: convertMinutesToCostString(pMeetings * 365 * (bCost - pCost), props.flowtraceUser.account),
    }
    const componentTitle = props.title ? props.title : "No title";

    return (
        <Grid container spacing={0}>
            <Grid item xs={4}> 
                <Grid container spacing={0}>
                    <Grid item xs={12}> <span className="text-white-90 font-size-l d-block mb-1 text-uppercase">{componentTitle}</span> </Grid>
                    <Grid item xs={12}> {getTitle("Meetings", "Number of meetings during the time period.")} </Grid>
                    <Grid item xs={12}> {getTitle("Costs", "Anticipated costs per period. " + costExplain)} </Grid>
                    <Grid item xs={12}> {getTitle("Costs (Baseline)", "Anticipated costs per period using baseline meeting characteristics and post-deployment meeting frequency. " + costBaselineExplain)} </Grid>
                    <Grid item xs={12}> {getTitle("Saving Estimate", "Anticipated savings per period compared to baseline meeting characteristics. " + saveExplain)} </Grid>
                </Grid>
            </Grid>
            <Grid item xs={2}> 
                <Grid container spacing={0}>
                    <Grid item xs={12}> <span className="text-white-90 font-size-l d-block mb-1 text-uppercase">Day</span> </Grid>
                    <Grid item xs={12}> {day.meetings} </Grid>
                    <Grid item xs={12}> {day.costString} </Grid>
                    <Grid item xs={12}> {day.costBaselineString} </Grid>
                    <Grid item xs={12}> {day.costSavingString} </Grid>
                </Grid>
            </Grid>
            <Grid item xs={2}> 
                <Grid container spacing={0}>
                    <Grid item xs={12}> <span className="text-white-90 font-size-l d-block mb-1 text-uppercase">Month</span> </Grid>
                    <Grid item xs={12}> {month.meetings} </Grid>
                    <Grid item xs={12}> {month.costString} </Grid>
                    <Grid item xs={12}> {month.costBaselineString} </Grid>
                    <Grid item xs={12}> {month.costSavingString} </Grid>
                </Grid>
            </Grid>
            <Grid item xs={3}> 
                <Grid container spacing={0}>
                    <Grid item xs={12}> <span className="text-white-90 font-size-l d-block mb-1 text-uppercase">Year</span> </Grid>
                    <Grid item xs={12}> {year.meetings} </Grid>
                    <Grid item xs={12}> {year.costString} </Grid>
                    <Grid item xs={12}> {year.costBaselineString} </Grid>
                    <Grid item xs={12}> {year.costSavingString} </Grid>
                </Grid>
            </Grid>
        </Grid>
    )
}
  
export default function StatsCard(props) {


    
    const iconName = props.type === "roi" ? 'funnel-dollar' : props.values.icon;
    const componentIcon = <FontAwesomeIcon icon={['fa', (iconName)]} className="font-size-xl" />;
    const componentColor = props.background ? props.background : "bg-info"
    const componentGrid = props.type === "roi" ? getRoiGrid(props) : getAttributeGrid(props);

    return (
        <Fragment>
            <Card className={componentColor + " card-box border-0 text-light mr-3 mb-3"}>
                <CardContent className="p-2">
                    <div className="d-flex align-items-start">
                        <div className="font-weight-bold">
                            {componentGrid}
                        </div>
                        <div className="ml-auto">
                            <div className="bg-white text-center text-primary d-50 rounded-circle d-flex align-items-center justify-content-center">
                                {componentIcon}
                            </div>
                        </div>
                    </div>
                </CardContent>
            </Card>
        </Fragment>
    );
}
